// packages/shared-components/src/utils/keyCodeMapper.ts
import type { AllKeyboardType } from "@eweadn-qiankun/shared-types";
import {
  LAYOUT_DK75_SINGLE_KEYCODES,
  LAYOUT_DK87_KEYCODES,
  LAYOUT_DK63_MACHINE_KEYCODES,
} from "@eweadn-qiankun/shared-utils";
import {
  LAYOUT_X98_1K_KEYCODES,
  LAYOUT_RONGYUAN87_KEYCODES,
} from "../components/Keyboard/keyboardLayouts";
import { KEYCODE_MAP_HFD } from "./keyCodeMapperHfd";
/**
 * KeyCode 映射表（基于 USB HID 规范和自定义扩展）
 * 数字 KeyCode → 按键名称和信息
 */

export interface KeyCodeInfo {
  /** 显示名称 */
  name: string;
  /** 类型：基础/系统/多媒体/鼠标/控制/灯光/三模/宏/特殊 */
  type: string;
  /** 备注说明 */
  comm: string;
  /** 16进制表示 */
  hex16: string;
  /** 分类 */
  typeof:
    | "basic"
    | "special"
    | "system"
    | "media"
    | "mouse"
    | "control"
    | "light"
    | "triMode"
    | "macro"
    | "game";
  index?: number;
}

/** KeyCode → KeyCodeInfo 映射 */
export const KEYCODE_MAP: Record<number, KeyCodeInfo> = {
  33554543: {
    name: "屏幕亮度增加",
    type: "特殊",
    comm: "屏幕亮度增加",
    hex16: "1FFBC6F",
    typeof: "special",
  },
  33554544: {
    name: "屏幕亮度减少",
    type: "特殊",
    comm: "屏幕亮度减少",
    hex16: "1FFBC70",
    typeof: "special",
  },
  33554979: {
    name: "浏览器",
    type: "特殊",
    comm: "浏览器",
    hex16: "1FFBE43",
    typeof: "special",
  },
  33554819: {
    name: "System Multimedia",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.systemMultimedia",
    hex16: "0x200143",
    typeof: "media",
  },
  33554615: {
    name: "Pause",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.pauseMedia",
    hex16: "0x200127",
    typeof: "media",
  },
  33554826: {
    name: "Email",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.email",
    hex16: "0x20014A",
    typeof: "media",
  },
  33554986: {
    name: "Favorites",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.favorites",
    hex16: "0x2001EA",
    typeof: "media",
  },
  0: {
    name: " ",
    type: "特殊",
    comm: "空键",
    hex16: "0x0000",
    typeof: "special",
  },
  1: {
    name: "△",
    type: "特殊",
    comm: "穿透键",
    hex16: "0x0001",
    typeof: "special",
  },
  4: {
    name: "A",
    type: "基础",
    comm: "",
    hex16: "0x0004",
    typeof: "basic",
  },
  5: {
    name: "B",
    type: "基础",
    comm: "",
    hex16: "0x0005",
    typeof: "basic",
  },
  6: {
    name: "C",
    type: "基础",
    comm: "",
    hex16: "0x0006",
    typeof: "basic",
  },
  7: {
    name: "D",
    type: "基础",
    comm: "",
    hex16: "0x0007",
    typeof: "basic",
  },
  8: {
    name: "E",
    type: "基础",
    comm: "",
    hex16: "0x0008",
    typeof: "basic",
  },
  9: {
    name: "F",
    type: "基础",
    comm: "",
    hex16: "0x0009",
    typeof: "basic",
  },
  10: {
    name: "G",
    type: "基础",
    comm: "",
    hex16: "0x000A",
    typeof: "basic",
  },
  11: {
    name: "H",
    type: "基础",
    comm: "",
    hex16: "0x000B",
    typeof: "basic",
  },
  12: {
    name: "I",
    type: "基础",
    comm: "",
    hex16: "0x000C",
    typeof: "basic",
  },
  13: {
    name: "J",
    type: "基础",
    comm: "",
    hex16: "0x000D",
    typeof: "basic",
  },
  14: {
    name: "K",
    type: "基础",
    comm: "",
    hex16: "0x000E",
    typeof: "basic",
  },
  15: {
    name: "L",
    type: "基础",
    comm: "",
    hex16: "0x000F",
    typeof: "basic",
  },
  16: {
    name: "M",
    type: "基础",
    comm: "",
    hex16: "0x0010",
    typeof: "basic",
  },
  17: {
    name: "N",
    type: "基础",
    comm: "",
    hex16: "0x0011",
    typeof: "basic",
  },
  18: {
    name: "O",
    type: "基础",
    comm: "",
    hex16: "0x0012",
    typeof: "basic",
  },
  19: {
    name: "P",
    type: "基础",
    comm: "",
    hex16: "0x0013",
    typeof: "basic",
  },
  20: {
    name: "Q",
    type: "基础",
    comm: "",
    hex16: "0x0014",
    typeof: "basic",
  },
  21: {
    name: "R",
    type: "基础",
    comm: "",
    hex16: "0x0015",
    typeof: "basic",
  },
  22: {
    name: "S",
    type: "基础",
    comm: "",
    hex16: "0x0016",
    typeof: "basic",
  },
  23: {
    name: "T",
    type: "基础",
    comm: "",
    hex16: "0x0017",
    typeof: "basic",
  },
  24: {
    name: "U",
    type: "基础",
    comm: "",
    hex16: "0x0018",
    typeof: "basic",
  },
  25: {
    name: "V",
    type: "基础",
    comm: "",
    hex16: "0x0019",
    typeof: "basic",
  },
  26: {
    name: "W",
    type: "基础",
    comm: "",
    hex16: "0x001A",
    typeof: "basic",
  },
  27: {
    name: "X",
    type: "基础",
    comm: "",
    hex16: "0x001B",
    typeof: "basic",
  },
  28: {
    name: "Y",
    type: "基础",
    comm: "",
    hex16: "0x001C",
    typeof: "basic",
  },
  29: {
    name: "Z",
    type: "基础",
    comm: "",
    hex16: "0x001D",
    typeof: "basic",
  },
  30: {
    name: "1 !",
    type: "基础",
    comm: "",
    hex16: "0x001E",
    typeof: "basic",
  },
  31: {
    name: "2 @",
    type: "基础",
    comm: "",
    hex16: "0x001F",
    typeof: "basic",
  },
  32: {
    name: "3 #",
    type: "基础",
    comm: "",
    hex16: "0x0020",
    typeof: "basic",
  },
  33: {
    name: "4 $",
    type: "基础",
    comm: "",
    hex16: "0x0021",
    typeof: "basic",
  },
  34: {
    name: "5 %",
    type: "基础",
    comm: "",
    hex16: "0x0022",
    typeof: "basic",
  },
  35: {
    name: "6 ^",
    type: "基础",
    comm: "",
    hex16: "0x0023",
    typeof: "basic",
  },
  36: {
    name: "7 &",
    type: "基础",
    comm: "",
    hex16: "0x0024",
    typeof: "basic",
  },
  37: {
    name: "8 *",
    type: "基础",
    comm: "",
    hex16: "0x0025",
    typeof: "basic",
  },
  38: {
    name: "9 (",
    type: "基础",
    comm: "",
    hex16: "0x0026",
    typeof: "basic",
  },
  39: {
    name: "0 )",
    type: "基础",
    comm: "",
    hex16: "0x0027",
    typeof: "basic",
  },
  40: {
    name: "Enter",
    type: "基础",
    comm: "",
    hex16: "0x0028",
    typeof: "basic",
  },
  41: {
    name: "Esc",
    type: "基础",
    comm: "",
    hex16: "0x0029",
    typeof: "basic",
  },
  42: {
    name: "Backspace",
    type: "基础",
    comm: "",
    hex16: "0x002A",
    typeof: "basic",
  },
  43: {
    name: "Tab",
    type: "基础",
    comm: "",
    hex16: "0x002B",
    typeof: "basic",
  },
  44: {
    name: "Space",
    type: "基础",
    comm: "",
    hex16: "0x002C",
    typeof: "basic",
  },
  45: {
    name: "- _",
    type: "基础",
    comm: "",
    hex16: "0x002D",
    typeof: "basic",
  },
  46: {
    name: "= +",
    type: "基础",
    comm: "",
    hex16: "0x002E",
    typeof: "basic",
  },
  47: {
    name: "[ {",
    type: "基础",
    comm: "",
    hex16: "0x002F",
    typeof: "basic",
  },
  48: {
    name: "] }",
    type: "基础",
    comm: "",
    hex16: "0x0030",
    typeof: "basic",
  },
  49: {
    name: "\\ |",
    type: "基础",
    comm: "",
    hex16: "0x0031",
    typeof: "basic",
  },
  50: {
    name: "# ~",
    type: "基础",
    comm: "",
    hex16: "0x0032",
    typeof: "basic",
  },
  51: {
    name: "; :",
    type: "基础",
    comm: "",
    hex16: "0x0033",
    typeof: "basic",
  },
  52: {
    name: "' \"",
    type: "基础",
    comm: "",
    hex16: "0x0034",
    typeof: "basic",
  },
  53: {
    name: "` ~",
    type: "基础",
    comm: "",
    hex16: "0x0035",
    typeof: "basic",
  },
  54: {
    name: ", <",
    type: "基础",
    comm: "",
    hex16: "0x0036",
    typeof: "basic",
  },
  55: {
    name: ". >",
    type: "基础",
    comm: "",
    hex16: "0x0037",
    typeof: "basic",
  },
  56: {
    name: "/ ?",
    type: "基础",
    comm: "",
    hex16: "0x0038",
    typeof: "basic",
  },
  57: {
    name: "Caps",
    type: "基础",
    comm: "",
    hex16: "0x0039",
    typeof: "basic",
  },
  58: {
    name: "F1",
    type: "基础",
    comm: "",
    hex16: "0x003A",
    typeof: "basic",
  },
  59: {
    name: "F2",
    type: "基础",
    comm: "",
    hex16: "0x003B",
    typeof: "basic",
  },
  60: {
    name: "F3",
    type: "基础",
    comm: "",
    hex16: "0x003C",
    typeof: "basic",
  },
  61: {
    name: "F4",
    type: "基础",
    comm: "",
    hex16: "0x003D",
    typeof: "basic",
  },
  62: {
    name: "F5",
    type: "基础",
    comm: "",
    hex16: "0x003E",
    typeof: "basic",
  },
  63: {
    name: "F6",
    type: "基础",
    comm: "",
    hex16: "0x003F",
    typeof: "basic",
  },
  64: {
    name: "F7",
    type: "基础",
    comm: "",
    hex16: "0x0040",
    typeof: "basic",
  },
  65: {
    name: "F8",
    type: "基础",
    comm: "",
    hex16: "0x0041",
    typeof: "basic",
  },
  66: {
    name: "F9",
    type: "基础",
    comm: "",
    hex16: "0x0042",
    typeof: "basic",
  },
  67: {
    name: "F10",
    type: "基础",
    comm: "",
    hex16: "0x0043",
    typeof: "basic",
  },
  68: {
    name: "F11",
    type: "基础",
    comm: "",
    hex16: "0x0044",
    typeof: "basic",
  },
  69: {
    name: "F12",
    type: "基础",
    comm: "",
    hex16: "0x0045",
    typeof: "basic",
  },
  70: {
    name: "PrtScr",
    type: "基础",
    comm: "",
    hex16: "0x0046",
    typeof: "basic",
  },
  71: {
    name: "Scroll",
    type: "基础",
    comm: "",
    hex16: "0x0047",
    typeof: "basic",
  },
  72: {
    name: "Pause",
    type: "基础",
    comm: "",
    hex16: "0x0048",
    typeof: "basic",
  },
  73: {
    name: "Insert",
    type: "基础",
    comm: "",
    hex16: "0x0049",
    typeof: "basic",
  },
  74: {
    name: "Home",
    type: "基础",
    comm: "",
    hex16: "0x004A",
    typeof: "basic",
  },
  75: {
    name: "PgUp",
    type: "基础",
    comm: "",
    hex16: "0x004B",
    typeof: "basic",
  },
  76: {
    name: "Delete",
    type: "基础",
    comm: "",
    hex16: "0x004C",
    typeof: "basic",
  },
  77: {
    name: "End",
    type: "基础",
    comm: "",
    hex16: "0x004D",
    typeof: "basic",
  },
  78: {
    name: "PgDn",
    type: "基础",
    comm: "",
    hex16: "0x004E",
    typeof: "basic",
  },
  79: {
    name: "→",
    type: "基础",
    comm: "",
    hex16: "0x004F",
    typeof: "basic",
  },
  80: {
    name: "←",
    type: "基础",
    comm: "",
    hex16: "0x0050",
    typeof: "basic",
  },
  81: {
    name: "↓",
    type: "基础",
    comm: "",
    hex16: "0x0051",
    typeof: "basic",
  },
  82: {
    name: "↑",
    type: "基础",
    comm: "",
    hex16: "0x0052",
    typeof: "basic",
  },
  83: {
    name: "Num Lock",
    type: "基础",
    comm: "",
    hex16: "0x0053",
    typeof: "basic",
  },
  84: {
    name: "/',",
    type: "基础",
    comm: "",
    hex16: "0x0054",
    typeof: "basic",
  },
  85: {
    name: "*',",
    type: "基础",
    comm: "",
    hex16: "0x0055",
    typeof: "basic",
  },
  86: {
    name: "-',",
    type: "基础",
    comm: "",
    hex16: "0x0056",
    typeof: "basic",
  },
  87: {
    name: "+',",
    type: "基础",
    comm: "",
    hex16: "0x0057",
    typeof: "basic",
  },
  88: {
    name: "PEnter",
    type: "基础",
    comm: "",
    hex16: "0x0058",
    typeof: "basic",
  },
  89: {
    name: "P1",
    type: "基础",
    comm: "",
    hex16: "0x0059",
    typeof: "basic",
  },
  90: {
    name: "P2",
    type: "基础",
    comm: "",
    hex16: "0x005A",
    typeof: "basic",
  },
  91: {
    name: "P3",
    type: "基础",
    comm: "",
    hex16: "0x005B",
    typeof: "basic",
  },
  92: {
    name: "P4",
    type: "基础",
    comm: "",
    hex16: "0x005C",
    typeof: "basic",
  },
  93: {
    name: "P5",
    type: "基础",
    comm: "",
    hex16: "0x005D",
    typeof: "basic",
  },
  94: {
    name: "P6",
    type: "基础",
    comm: "",
    hex16: "0x005E",
    typeof: "basic",
  },
  95: {
    name: "P7",
    type: "基础",
    comm: "",
    hex16: "0x005F",
    typeof: "basic",
  },
  96: {
    name: "P8",
    type: "基础",
    comm: "",
    hex16: "0x0060",
    typeof: "basic",
  },
  97: {
    name: "P9",
    type: "基础",
    comm: "",
    hex16: "0x0061",
    typeof: "basic",
  },
  98: {
    name: "P0",
    type: "基础",
    comm: "",
    hex16: "0x0062",
    typeof: "basic",
  },
  99: {
    name: ". Del",
    type: "基础",
    comm: "",
    hex16: "0x0063",
    typeof: "basic",
  },
  100: {
    name: "Nubs",
    type: "基础",
    comm: "",
    hex16: "0x0064",
    typeof: "basic",
  },
  101: {
    name: "App",
    type: "基础",
    comm: "",
    hex16: "0x0065",
    typeof: "basic",
  },
  102: {
    name: "Power",
    type: "基础",
    comm: "",
    hex16: "0x0066",
    typeof: "basic",
  },
  103: {
    name: "Peql",
    type: "基础",
    comm: "",
    hex16: "0x0067",
    typeof: "basic",
  },
  104: {
    name: "F13",
    type: "基础",
    comm: "",
    hex16: "0x0068",
    typeof: "basic",
  },
  105: {
    name: "F14",
    type: "基础",
    comm: "",
    hex16: "0x0069",
    typeof: "basic",
  },
  106: {
    name: "F15",
    type: "基础",
    comm: "",
    hex16: "0x006A",
    typeof: "basic",
  },
  107: {
    name: "F16",
    type: "基础",
    comm: "",
    hex16: "0x006B",
    typeof: "basic",
  },
  108: {
    name: "F17",
    type: "基础",
    comm: "",
    hex16: "0x006C",
    typeof: "basic",
  },
  109: {
    name: "F18",
    type: "基础",
    comm: "",
    hex16: "0x006D",
    typeof: "basic",
  },
  110: {
    name: "F19",
    type: "基础",
    comm: "",
    hex16: "0x006E",
    typeof: "basic",
  },
  111: {
    name: "F20",
    type: "基础",
    comm: "",
    hex16: "0x006F",
    typeof: "basic",
  },
  112: {
    name: "F21",
    type: "基础",
    comm: "",
    hex16: "0x0070",
    typeof: "basic",
  },
  113: {
    name: "F22",
    type: "基础",
    comm: "",
    hex16: "0x0071",
    typeof: "basic",
  },
  114: {
    name: "F23",
    type: "基础",
    comm: "",
    hex16: "0x0072",
    typeof: "basic",
  },
  115: {
    name: "F24",
    type: "基础",
    comm: "",
    hex16: "0x0073",
    typeof: "basic",
  },
  135: {
    name: "\\ ろ",
    type: "基础",
    comm: "",
    hex16: "0x0087",
    typeof: "basic",
  },
  138: {
    name: "変換",
    type: "基础",
    comm: "",
    hex16: "0x008A",
    typeof: "basic",
  },
  139: {
    name: "無変換",
    type: "基础",
    comm: "",
    hex16: "0x008B",
    typeof: "basic",
  },
  224: {
    name: "L-Ctrl",
    type: "基础",
    comm: "",
    hex16: "0x00E0",
    typeof: "basic",
  },
  225: {
    name: "L-Shift",
    type: "基础",
    comm: "",
    hex16: "0x00E1",
    typeof: "basic",
  },
  226: {
    name: "L-Alt",
    type: "基础",
    comm: "",
    hex16: "0x00E2",
    typeof: "basic",
  },
  227: {
    name: "Win",
    type: "基础",
    comm: "",
    hex16: "0x00E3",
    typeof: "basic",
  },
  228: {
    name: "R-Ctrl",
    type: "基础",
    comm: "",
    hex16: "0x00E4",
    typeof: "basic",
  },
  229: {
    name: "R-Shift",
    type: "基础",
    comm: "",
    hex16: "0x00E5",
    typeof: "basic",
  },
  230: {
    name: "R-Alt",
    type: "基础",
    comm: "",
    hex16: "0x00E6",
    typeof: "basic",
  },
  231: {
    name: "R-Win",
    type: "基础",
    comm: "",
    hex16: "0x00E7",
    typeof: "basic",
  },
  4434: {
    name: "MCON",
    type: "系统",
    comm: "MacOS控制台",
    hex16: "0x1152",
    typeof: "system",
  },
  4905: {
    name: "WTMG",
    type: "系统",
    comm: "Windows任务管理器",
    hex16: "0x1329",
    typeof: "system",
  },
  6151: {
    name: "WDSK",
    type: "系统",
    comm: "Windows桌面",
    hex16: "0x1807",
    typeof: "system",
  },
  6152: {
    name: "WFIL",
    type: "系统",
    comm: "Windows文件浏览器",
    hex16: "0x1808",
    typeof: "system",
  },
  6165: {
    name: "WCMD",
    type: "系统",
    comm: "Windows运行",
    hex16: "0x1815",
    typeof: "system",
  },
  8303: {
    name: "Bri+",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.brightnessUp",
    hex16: "0x206F",
    typeof: "media",
  },
  8304: {
    name: "Bri-",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.brightnessDown",
    hex16: "0x2070",
    typeof: "media",
  },
  8373: {
    name: "Next",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.nextTrack",
    hex16: "0x20B5",
    typeof: "media",
  },
  8374: {
    name: "Prev",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.prevTrack",
    hex16: "0x20B6",
    typeof: "media",
  },
  8375: {
    name: "Stop",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.stop",
    hex16: "0x20B7",
    typeof: "media",
  },
  8397: {
    name: "Play",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.playPause",
    hex16: "0x20CD",
    typeof: "media",
  },
  8418: {
    name: "Mute",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.mute",
    hex16: "0x20E2",
    typeof: "media",
  },
  8425: {
    name: "Vol+",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.volumeUp",
    hex16: "0x20E9",
    typeof: "media",
  },
  8426: {
    name: "Vol-",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.volumeDown",
    hex16: "0x20EA",
    typeof: "media",
  },
  8579: {
    name: "Music",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.music",
    hex16: "0x2183",
    typeof: "media",
  },
  8586: {
    name: "Mail",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.mail",
    hex16: "0x218A",
    typeof: "media",
  },
  8433: {
    name: "MicMute",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.micMute",
    hex16: "0x20F1",
    typeof: "media",
  },
  8594: {
    name: "Calc",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.calculator",
    hex16: "0x2192",
    typeof: "media",
  },
  8595: {
    name: "AppSw",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.appSwitch",
    hex16: "0x2193",
    typeof: "media",
  },
  /** 8815 getKeyboardKeys 计算器键 `value`，与布局星闪码 8594 对应 */
  33554834: {
    name: "Calc",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.calculator",
    hex16: "0x200152",
    typeof: "media",
  },
  /** X98 8815 扩展槽（RAW pos 516，名称由设备可空） */
  23205: {
    name: "扩展",
    type: "特殊",
    comm: "",
    hex16: "0x5AA5",
    typeof: "special",
  },
  8596: {
    name: "Mute",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.myComputer",
    hex16: "0x2194",
    typeof: "media",
  },
  8739: {
    name: "Vol+",
    type: "多媒体",
    comm: "keyboard.keyFunction.media.browser",
    hex16: "0x2223",
    typeof: "media",
  },
  16384: {
    name: "M-Free",
    type: "鼠标",
    comm: "keyboard.keyFunction.mouse.release",
    hex16: "0x4000",
    typeof: "mouse",
  },
  16640: {
    name: "M-Left",
    type: "鼠标",
    comm: "keyboard.keyFunction.mouse.leftClick",
    hex16: "0x4100",
    typeof: "mouse",
  },
  16896: {
    name: "M-Right",
    type: "鼠标",
    comm: "keyboard.keyFunction.mouse.rightClick",
    hex16: "0x4200",
    typeof: "mouse",
  },
  17152: {
    name: "M-Middle",
    type: "鼠标",
    comm: "keyboard.keyFunction.mouse.middleClick",
    hex16: "0x4300",
    typeof: "mouse",
  },
  17408: {
    name: "M-FWD",
    type: "鼠标",
    comm: "鼠标Forward",
    hex16: "0x4400",
    typeof: "mouse",
  },
  17664: {
    name: "M-BACK",
    type: "鼠标",
    comm: "鼠标Back",
    hex16: "0x4500",
    typeof: "mouse",
  },
  17920: {
    name: "M-MLX",
    type: "鼠标",
    comm: "keyboard.keyFunction.mouse.moveLeft",
    hex16: "0x4600",
    typeof: "mouse",
  },
  18176: {
    name: "M-MRX",
    type: "鼠标",
    comm: "keyboard.keyFunction.mouse.moveRight",
    hex16: "0x4700",
    typeof: "mouse",
  },
  18432: {
    name: "M-MUY",
    type: "鼠标",
    comm: "keyboard.keyFunction.mouse.moveUp",
    hex16: "0x4800",
    typeof: "mouse",
  },
  18688: {
    name: "M-MDY",
    type: "鼠标",
    comm: "keyboard.keyFunction.mouse.moveDown",
    hex16: "0x4900",
    typeof: "mouse",
  },
  18945: {
    name: "M-WHF",
    type: "鼠标",
    comm: "keyboard.keyFunction.mouse.wheelForward",
    hex16: "0x4A01",
    typeof: "mouse",
  },
  19201: {
    name: "M-WHB",
    type: "鼠标",
    comm: "keyboard.keyFunction.mouse.wheelBackward",
    hex16: "0x4B01",
    typeof: "mouse",
  },
  61696: {
    name: "Fn0",
    type: "特殊",
    comm: "切换主层",
    hex16: "0xF100",
    typeof: "special",
  },
  61697: {
    name: "Fn1",
    type: "特殊",
    comm: "切换Fn1层",
    hex16: "0xF101",
    typeof: "special",
  },
  61698: {
    name: "Fn2",
    type: "特殊",
    comm: "切换Fn2层",
    hex16: "0xF102",
    typeof: "special",
  },
  61699: {
    name: "Fn3",
    type: "特殊",
    comm: "切换Fn3层",
    hex16: "0xF103",
    typeof: "special",
  },
  61952: {
    name: "Reset",
    type: "控制",
    comm: "keyboard.keyFunction.control.factoryReset",
    hex16: "0xF200",
    typeof: "control",
  },
  61953: {
    name: "Win",
    type: "控制",
    comm: "keyboard.keyFunction.control.switchToWin",
    hex16: "0xF201",
    typeof: "control",
  },
  61954: {
    name: "Mac",
    type: "控制",
    comm: "keyboard.keyFunction.control.switchToMac",
    hex16: "0xF202",
    typeof: "control",
  },
  61955: {
    name: "Adj",
    type: "控制",
    comm: "keyboard.keyFunction.control.calibrate",
    hex16: "0xF203",
    typeof: "control",
  },
  61957: {
    name: "Config1",
    type: "控制",
    comm: "keyboard.keyFunction.control.config1",
    hex16: "0xF205",
    typeof: "control",
  },
  61958: {
    name: "Config2",
    type: "控制",
    comm: "keyboard.keyFunction.control.config2",
    hex16: "0xF206",
    typeof: "control",
  },
  61959: {
    name: "Config3",
    type: "控制",
    comm: "keyboard.keyFunction.control.config3",
    hex16: "0xF207",
    typeof: "control",
  },
  61960: {
    name: "Config4",
    type: "控制",
    comm: "keyboard.keyFunction.control.config4",
    hex16: "0xF208",
    typeof: "control",
  },
  61961: {
    name: "LockWin",
    type: "控制",
    comm: "keyboard.keyFunction.control.lockWin",
    hex16: "0xF209",
    typeof: "control",
  },
  62208: {
    name: "Mode",
    type: "灯光",
    comm: "keyboard.keyFunction.light.modeSwitch",
    hex16: "0xF300",
    typeof: "light",
  },
  62209: {
    name: "Color",
    type: "灯光",
    comm: "keyboard.keyFunction.light.colorSwitch",
    hex16: "0xF301",
    typeof: "light",
  },
  62210: {
    name: "Bri+",
    type: "灯光",
    comm: "keyboard.keyFunction.light.brightnessUp",
    hex16: "0xF302",
    typeof: "light",
  },
  62211: {
    name: "Bri-",
    type: "灯光",
    comm: "keyboard.keyFunction.light.brightnessDown",
    hex16: "0xF303",
    typeof: "light",
  },
  62212: {
    name: "Spd+",
    type: "灯光",
    comm: "keyboard.keyFunction.light.speedUp",
    hex16: "0xF304",
    typeof: "light",
  },
  62213: {
    name: "Spd-",
    type: "灯光",
    comm: "keyboard.keyFunction.light.speedDown",
    hex16: "0xF305",
    typeof: "light",
  },
  62214: {
    name: "Prev",
    type: "灯光",
    comm: "keyboard.keyFunction.light.prevMode",
    hex16: "0xF306",
    typeof: "light",
  },
  62215: {
    name: "OnOff",
    type: "灯光",
    comm: "keyboard.keyFunction.light.onOff",
    hex16: "0xF307",
    typeof: "light",
  },
  62216: {
    name: "Dir",
    type: "灯光",
    comm: "keyboard.keyFunction.light.direction",
    hex16: "0xF308",
    typeof: "light",
  },
  62224: {
    name: "D1_Mode",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco1ModeSwitch",
    hex16: "0xF310",
    typeof: "light",
  },
  62225: {
    name: "D1_Color",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco1ColorSwitch",
    hex16: "0xF311",
    typeof: "light",
  },
  62226: {
    name: "D1_Bri+",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco1BrightnessUp",
    hex16: "0xF312",
    typeof: "light",
  },
  62227: {
    name: "D1_Bri-",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco1BrightnessDown",
    hex16: "0xF313",
    typeof: "light",
  },
  62228: {
    name: "D1_Spd+",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco1SpeedUp",
    hex16: "0xF314",
    typeof: "light",
  },
  62229: {
    name: "D1_Spd-",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco1SpeedDown",
    hex16: "0xF315",
    typeof: "light",
  },
  62230: {
    name: "D1_Prev",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco1PrevMode",
    hex16: "0xF316",
    typeof: "light",
  },
  62231: {
    name: "D1_OnOff",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco1OnOff",
    hex16: "0xF317",
    typeof: "light",
  },
  62232: {
    name: "D1_Dir",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco1Direction",
    hex16: "0xF318",
    typeof: "light",
  },
  62240: {
    name: "D2_Mode",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco2ModeSwitch",
    hex16: "0xF320",
    typeof: "light",
  },
  62241: {
    name: "D2_Color",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco2ColorSwitch",
    hex16: "0xF321",
    typeof: "light",
  },
  62242: {
    name: "D2_Bri+",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco2BrightnessUp",
    hex16: "0xF322",
    typeof: "light",
  },
  62243: {
    name: "D2_Bri-",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco2BrightnessDown",
    hex16: "0xF323",
    typeof: "light",
  },
  62244: {
    name: "D2_Spd+",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco2SpeedUp",
    hex16: "0xF324",
    typeof: "light",
  },
  62245: {
    name: "D2_Spd-",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco2SpeedDown",
    hex16: "0xF325",
    typeof: "light",
  },
  62246: {
    name: "D2_Prev",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco2PrevMode",
    hex16: "0xF326",
    typeof: "light",
  },
  62247: {
    name: "D2_OnOff",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco2OnOff",
    hex16: "0xF327",
    typeof: "light",
  },
  62248: {
    name: "D2_Dir",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco2Direction",
    hex16: "0xF328",
    typeof: "light",
  },
  62256: {
    name: "D3_Mode",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco3ModeSwitch",
    hex16: "0xF330",
    typeof: "light",
  },
  62257: {
    name: "D3_Color",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco3ColorSwitch",
    hex16: "0xF331",
    typeof: "light",
  },
  62258: {
    name: "D3_Bri+",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco3BrightnessUp",
    hex16: "0xF332",
    typeof: "light",
  },
  62259: {
    name: "D3_Bri-",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco3BrightnessDown",
    hex16: "0xF333",
    typeof: "light",
  },
  62260: {
    name: "D3_Spd+",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco3SpeedUp",
    hex16: "0xF334",
    typeof: "light",
  },
  62261: {
    name: "D3_Spd-",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco3SpeedDown",
    hex16: "0xF335",
    typeof: "light",
  },
  62262: {
    name: "D3_Prev",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco3PrevMode",
    hex16: "0xF336",
    typeof: "light",
  },
  62263: {
    name: "D3_OnOff",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco3OnOff",
    hex16: "0xF337",
    typeof: "light",
  },
  62264: {
    name: "D3_Dir",
    type: "灯光",
    comm: "keyboard.keyFunction.light.deco3Direction",
    hex16: "0xF338",
    typeof: "light",
  },
  62338: {
    name: "Ctrl + ↓",
    type: "组合键",
    comm: "Ctrl + ↓",
    hex16: "0xF382",
    typeof: "system",
  },
  62376: {
    name: "Win + Space",
    type: "组合键",
    comm: "Win + Space",
    hex16: "0xF3A8",
    typeof: "system",
  },
  62380: {
    name: "Alt + Space",
    type: "组合键",
    comm: "Alt + Space",
    hex16: "0xF3AC",
    typeof: "system",
  },
  62465: {
    name: "USB",
    type: "三模",
    comm: "切换至USB模式",
    hex16: "0xF401",
    typeof: "triMode",
  },
  62466: {
    name: "24G",
    type: "三模",
    comm: "切换至2.4G模式（短按切换，长按配对）",
    hex16: "0xF402",
    typeof: "triMode",
  },
  62467: {
    name: "BLE1",
    type: "三模",
    comm: "切换至蓝牙1模式（短按切换，长按配对）",
    hex16: "0xF403",
    typeof: "triMode",
  },
  62468: {
    name: "BLE2",
    type: "三模",
    comm: "切换至蓝牙2模式（短按切换，长按配对）",
    hex16: "0xF404",
    typeof: "triMode",
  },
  62469: {
    name: "BLE3",
    type: "三模",
    comm: "切换至蓝牙3模式（短按切换，长按配对）",
    hex16: "0xF405",
    typeof: "triMode",
  },
  62470: {
    name: "Reset",
    type: "三模",
    comm: "清除配对数据",
    hex16: "0xF406",
    typeof: "triMode",
  },
  62471: {
    name: "Bat",
    type: "三模",
    comm: "查询电量",
    hex16: "0xF407",
    typeof: "triMode",
  },
  62720: {
    name: "M0",
    type: "快捷指令",
    comm: "",
    hex16: "0xF500",
    typeof: "macro",
    index: 0,
  },
  62721: {
    name: "M1",
    type: "快捷指令",
    comm: "",
    hex16: "0xF501",
    typeof: "macro",
    index: 1,
  },
  62722: {
    name: "M2",
    type: "快捷指令",
    comm: "",
    hex16: "0xF502",
    typeof: "macro",
    index: 2,
  },
  62723: {
    name: "M3",
    type: "快捷指令",
    comm: "",
    hex16: "0xF503",
    typeof: "macro",
    index: 3,
  },
  62724: {
    name: "M4",
    type: "快捷指令",
    comm: "",
    hex16: "0xF504",
    typeof: "macro",
    index: 4,
  },
  62725: {
    name: "M5",
    type: "快捷指令",
    comm: "",
    hex16: "0xF505",
    typeof: "macro",
    index: 5,
  },
  62726: {
    name: "M6",
    type: "快捷指令",
    comm: "",
    hex16: "0xF506",
    typeof: "macro",
    index: 6,
  },
  62727: {
    name: "M7",
    type: "快捷指令",
    comm: "",
    hex16: "0xF507",
    typeof: "macro",
    index: 7,
  },
  62728: {
    name: "M8",
    type: "快捷指令",
    comm: "",
    hex16: "0xF508",
    typeof: "macro",
    index: 8,
  },
  62729: {
    name: "M9",
    type: "快捷指令",
    comm: "",
    hex16: "0xF509",
    typeof: "macro",
    index: 9,
  },
  62730: {
    name: "M10",
    type: "快捷指令",
    comm: "",
    hex16: "0xF50A",
    typeof: "macro",
    index: 10,
  },
  62731: {
    name: "M11",
    type: "快捷指令",
    comm: "",
    hex16: "0xF50B",
    typeof: "macro",
    index: 11,
  },
  62732: {
    name: "M12",
    type: "快捷指令",
    comm: "",
    hex16: "0xF50C",
    typeof: "macro",
    index: 12,
  },
  62733: {
    name: "M13",
    type: "快捷指令",
    comm: "",
    hex16: "0xF50D",
    typeof: "macro",
    index: 13,
  },
  62734: {
    name: "M14",
    type: "快捷指令",
    comm: "",
    hex16: "0xF50E",
    typeof: "macro",
    index: 14,
  },
  62735: {
    name: "M15",
    type: "快捷指令",
    comm: "",
    hex16: "0xF50F",
    typeof: "macro",
    index: 15,
  },
  62736: {
    name: "M16",
    type: "快捷指令",
    comm: "",
    hex16: "0xF510",
    typeof: "macro",
    index: 16,
  },
  62737: {
    name: "M17",
    type: "快捷指令",
    comm: "",
    hex16: "0xF511",
    typeof: "macro",
    index: 17,
  },
  62738: {
    name: "M18",
    type: "快捷指令",
    comm: "",
    hex16: "0xF512",
    typeof: "macro",
    index: 18,
  },
  62739: {
    name: "M19",
    type: "快捷指令",
    comm: "",
    hex16: "0xF513",
    typeof: "macro",
    index: 19,
  },
  21264: { name: "A", type: "game", comm: "", hex16: "0x5310", typeof: "game" },
  21280: { name: "B", type: "game", comm: "", hex16: "0x5320", typeof: "game" },
  21312: { name: "X", type: "game", comm: "", hex16: "0x5340", typeof: "game" },
  21376: { name: "Y", type: "game", comm: "", hex16: "0x5380", typeof: "game" },
  21008: {
    name: "Start",
    type: "game",
    comm: "",
    hex16: "0x5210",
    typeof: "game",
  },
  21024: {
    name: "Select",
    type: "game",
    comm: "",
    hex16: "0x5220",
    typeof: "game",
  },
  21252: {
    name: "Menu",
    type: "game",
    comm: "",
    hex16: "0x5304",
    typeof: "game",
  },

  // 方向键
  20993: {
    name: "Direction UP",
    type: "game",
    comm: "",
    hex16: "0x5201",
    typeof: "game",
  },
  20994: {
    name: "Direction DOWN",
    type: "game",
    comm: "",
    hex16: "0x5202",
    typeof: "game",
  },
  20996: {
    name: "Direction LEFT",
    type: "game",
    comm: "",
    hex16: "0x5204",
    typeof: "game",
  },
  21000: {
    name: "Direction RIGHT",
    type: "game",
    comm: "",
    hex16: "0x5208",
    typeof: "game",
  },

  // 肩键
  21249: {
    name: "Left Bumper",
    type: "game",
    comm: "",
    hex16: "0x5301",
    typeof: "game",
  },
  21250: {
    name: "Right Bumper",
    type: "game",
    comm: "",
    hex16: "0x5302",
    typeof: "game",
  },
  21504: {
    name: "Left Trigger",
    type: "game",
    comm: "",
    hex16: "0x5400",
    typeof: "game",
  },
  21760: {
    name: "Right Trigger",
    type: "game",
    comm: "",
    hex16: "0x5500",
    typeof: "game",
  },

  // 左摇杆
  22529: {
    name: "Left Stick UP",
    type: "game",
    comm: "",
    hex16: "0x5801",
    typeof: "game",
  },
  22530: {
    name: "Left Stick DOWN",
    type: "game",
    comm: "",
    hex16: "0x5802",
    typeof: "game",
  },
  22018: {
    name: "Left Stick LEFT",
    type: "game",
    comm: "",
    hex16: "0x5602",
    typeof: "game",
  },
  22017: {
    name: "Left Stick RIGHT",
    type: "game",
    comm: "",
    hex16: "0x5601",
    typeof: "game",
  },
  21056: {
    name: "Left Stick PRESS",
    type: "game",
    comm: "",
    hex16: "0x5240",
    typeof: "game",
  },

  // 右摇杆
  23553: {
    name: "Right Stick UP",
    type: "game",
    comm: "",
    hex16: "0x5c01",
    typeof: "game",
  },
  23554: {
    name: "Right Stick DOWN",
    type: "game",
    comm: "",
    hex16: "0x5c02",
    typeof: "game",
  },
  23042: {
    name: "Right Stick LEFT",
    type: "game",
    comm: "",
    hex16: "0x5a02",
    typeof: "game",
  },
  23041: {
    name: "Right Stick RIGHT",
    type: "game",
    comm: "",
    hex16: "0x5a01",
    typeof: "game",
  },
  21120: {
    name: "Right Stick PRESS",
    type: "game",
    comm: "",
    hex16: "0x5280",
    typeof: "game",
  },

  // 经典手柄
  49153: {
    name: "GP_1",
    type: "game",
    comm: "",
    hex16: "0xC001",
    typeof: "game",
  },
  49154: {
    name: "GP_2",
    type: "game",
    comm: "",
    hex16: "0xC002",
    typeof: "game",
  },
  49156: {
    name: "GP_3",
    type: "game",
    comm: "",
    hex16: "0xC004",
    typeof: "game",
  },
  49160: {
    name: "GP_4",
    type: "game",
    comm: "",
    hex16: "0xC008",
    typeof: "game",
  },
  49168: {
    name: "GP_5",
    type: "game",
    comm: "",
    hex16: "0xC010",
    typeof: "game",
  },
  49184: {
    name: "GP_6",
    type: "game",
    comm: "",
    hex16: "0xC020",
    typeof: "game",
  },
  49216: {
    name: "GP_7",
    type: "game",
    comm: "",
    hex16: "0xC040",
    typeof: "game",
  },
  49280: {
    name: "GP_8",
    type: "game",
    comm: "",
    hex16: "0xC080",
    typeof: "game",
  },
  49409: {
    name: "GP_9",
    type: "game",
    comm: "",
    hex16: "0xC101",
    typeof: "game",
  },
  49410: {
    name: "GP_10",
    type: "game",
    comm: "",
    hex16: "0xC102",
    typeof: "game",
  },
  49412: {
    name: "GP_11",
    type: "game",
    comm: "",
    hex16: "0xC104",
    typeof: "game",
  },
  49416: {
    name: "GP_12",
    type: "game",
    comm: "",
    hex16: "0xC108",
    typeof: "game",
  },
  54017: {
    name: "GP_DIR_UP",
    type: "game",
    comm: "",
    hex16: "0xD301",
    typeof: "game",
  },
  54021: {
    name: "GP_DIR_DOWN",
    type: "game",
    comm: "",
    hex16: "0xD305",
    typeof: "game",
  },
  54023: {
    name: "GP_DIR_LEFT",
    type: "game",
    comm: "",
    hex16: "0xD307",
    typeof: "game",
  },
  54019: {
    name: "GP_DIR_RIGHT",
    type: "game",
    comm: "",
    hex16: "0xD303",
    typeof: "game",
  },
  52992: {
    name: "GP_E",
    type: "game",
    comm: "",
    hex16: "0xCF00",
    typeof: "game",
  },
  52993: {
    name: "GP_A",
    type: "game",
    comm: "",
    hex16: "0xCF01",
    typeof: "game",
  },
  52994: {
    name: "GP_R",
    type: "game",
    comm: "",
    hex16: "0xCF02",
    typeof: "game",
  },
  52995: {
    name: "GP_T",
    type: "game",
    comm: "",
    hex16: "0xCF03",
    typeof: "game",
  },
  52736: {
    name: "GP_TH",
    type: "game",
    comm: "",
    hex16: "0xCE00",
    typeof: "game",
  },
  49664: {
    name: "GP_XL",
    type: "game",
    comm: "",
    hex16: "0xC200",
    typeof: "game",
  },
  49665: {
    name: "GP_XR",
    type: "game",
    comm: "",
    hex16: "0xC201",
    typeof: "game",
  },
  50176: {
    name: "GP_YU",
    type: "game",
    comm: "",
    hex16: "0xC400",
    typeof: "game",
  },
  50177: {
    name: "GP_YD",
    type: "game",
    comm: "",
    hex16: "0xC401",
    typeof: "game",
  },
  50688: {
    name: "GP_ZU",
    type: "game",
    comm: "",
    hex16: "0xC600",
    typeof: "game",
  },
  50689: {
    name: "GP_ZD",
    type: "game",
    comm: "",
    hex16: "0xC601",
    typeof: "game",
  },
  51200: {
    name: "GP_ZFR",
    type: "game",
    comm: "",
    hex16: "0xC800",
    typeof: "game",
  },
  51201: {
    name: "GP_ZRR",
    type: "game",
    comm: "",
    hex16: "0xC801",
    typeof: "game",
  },
  51712: {
    name: "GP_XFR",
    type: "game",
    comm: "",
    hex16: "0xCA00",
    typeof: "game",
  },
  51713: {
    name: "GP_XRR",
    type: "game",
    comm: "",
    hex16: "0xCA01",
    typeof: "game",
  },
  52224: {
    name: "GP_YFR",
    type: "game",
    comm: "",
    hex16: "0xCC00",
    typeof: "game",
  },
  52225: {
    name: "GP_YRR",
    type: "game",
    comm: "",
    hex16: "0xCC01",
    typeof: "game",
  },
};

export const KEYCODE_MACRO_MAP: Record<number, KeyCodeInfo> = {
  62720: {
    name: "M0",
    type: "快捷指令",
    comm: "",
    hex16: "0xF500",
    typeof: "macro",
    index: 0,
  },
  62721: {
    name: "M1",
    type: "快捷指令",
    comm: "",
    hex16: "0xF501",
    typeof: "macro",
    index: 1,
  },
  62722: {
    name: "M2",
    type: "快捷指令",
    comm: "",
    hex16: "0xF502",
    typeof: "macro",
    index: 2,
  },
  62723: {
    name: "M3",
    type: "快捷指令",
    comm: "",
    hex16: "0xF503",
    typeof: "macro",
    index: 3,
  },
  62724: {
    name: "M4",
    type: "快捷指令",
    comm: "",
    hex16: "0xF504",
    typeof: "macro",
    index: 4,
  },
  62725: {
    name: "M5",
    type: "快捷指令",
    comm: "",
    hex16: "0xF505",
    typeof: "macro",
    index: 5,
  },
  62726: {
    name: "M6",
    type: "快捷指令",
    comm: "",
    hex16: "0xF506",
    typeof: "macro",
    index: 6,
  },
  62727: {
    name: "M7",
    type: "快捷指令",
    comm: "",
    hex16: "0xF507",
    typeof: "macro",
    index: 7,
  },
  62728: {
    name: "M8",
    type: "快捷指令",
    comm: "",
    hex16: "0xF508",
    typeof: "macro",
    index: 8,
  },
  62729: {
    name: "M9",
    type: "快捷指令",
    comm: "",
    hex16: "0xF509",
    typeof: "macro",
    index: 9,
  },
  62730: {
    name: "M10",
    type: "快捷指令",
    comm: "",
    hex16: "0xF50A",
    typeof: "macro",
    index: 10,
  },
  62731: {
    name: "M11",
    type: "快捷指令",
    comm: "",
    hex16: "0xF50B",
    typeof: "macro",
    index: 11,
  },
  62732: {
    name: "M12",
    type: "快捷指令",
    comm: "",
    hex16: "0xF50C",
    typeof: "macro",
    index: 12,
  },
  62733: {
    name: "M13",
    type: "快捷指令",
    comm: "",
    hex16: "0xF50D",
    typeof: "macro",
    index: 13,
  },
  62734: {
    name: "M14",
    type: "快捷指令",
    comm: "",
    hex16: "0xF50E",
    typeof: "macro",
    index: 14,
  },
  62735: {
    name: "M15",
    type: "快捷指令",
    comm: "",
    hex16: "0xF50F",
    typeof: "macro",
    index: 15,
  },
  62736: {
    name: "M16",
    type: "快捷指令",
    comm: "",
    hex16: "0xF510",
    typeof: "macro",
    index: 16,
  },
  62737: {
    name: "M17",
    type: "快捷指令",
    comm: "",
    hex16: "0xF511",
    typeof: "macro",
    index: 17,
  },
  62738: {
    name: "M18",
    type: "快捷指令",
    comm: "",
    hex16: "0xF512",
    typeof: "macro",
    index: 18,
  },
  62739: {
    name: "M19",
    type: "快捷指令",
    comm: "",
    hex16: "0xF513",
    typeof: "macro",
    index: 19,
  },
};

/**
 * ✅ 键盘布局数据（唯一数据源）
 * layoutData[row][col] = keyCode
 * 0 = 空键, 1 = 穿透键
 */
export const KEYBOARD_LAYOUTS: Record<AllKeyboardType, number[][]> = {
  /**
   * 63 键盘布局 (6 行 × 14 列)
   */
  "63": [
    // Row 0: 全空
    [0],
    // Row 1: Esc + 数字行 + Home (15 keys)
    [41, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42],
    // Row 2: Tab 行 + End (15 keys)
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49],
    // Row 3: Caps + Enter 前占位列（与 TOTAL_63_KEY_WIDTHS 14 列一致）
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40],
    // Row 4: LShift + 占位列 + Z 行；col11–13 与固件/SDK 一致：/?、↑、R-Shift
    [225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 82, 229],
    // Row 5: 底行空格 col6；公版 DK63：RAlt、←、↓、→、Fn（勿改成 V2 的 Fn←↓→）
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 230, 80, 81, 79, 61697],
  ],
  /** SDCX DK63 Machine：公版驱动出厂矩阵反推的 UI 键码 */
  dk63Machine: LAYOUT_DK63_MACHINE_KEYCODES,
  /**
   * 68 键盘布局 (6 行 × 21 列，真实硬件数据)
   */
  "68": [
    // Row 0: 全空 (21 keys)
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    // Row 1: Esc + 1-0 + -= + Bksp + Home (21 cols)
    [
      41, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 0, 0, 0, 0, 0,
      0,
    ],
    // Row 2: Tab + Q-P + []\ + End (21 cols)
    [
      43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 0, 0, 0, 0, 0,
      0,
    ],
    // Row 3: Caps + A-L + ;' + gap + Enter + PgUp (21 cols)
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 75, 0, 0, 0, 0, 0, 0],
    // Row 4: Shift + gap + Z-M + ,./ + Shift + ↑ + PgDn (21 cols)
    [
      225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78, 0, 0, 0, 0, 0,
      0,
    ],
    // Row 5: Ctrl Win Alt + gap×3 + Space + gap×2 + Alt Fn Ctrl + ←↓→ (21 cols)
    [
      224, 227, 226, 0, 0, 0, 44, 0, 0, 230, 61697, 228, 80, 81, 79, 0, 0, 0, 0,
      0, 0,
    ],
  ],
  /** 华奋达 BAT68：基于 DEEP68/`68`，最右侧自上而下为 Home / End / PgUp / PgDn */
  huafenda68: [
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [
      41, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 0, 0, 0, 0, 0,
      0,
    ],
    [
      43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 77, 0, 0, 0, 0, 0,
      0,
    ],
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 75, 0, 0, 0, 0, 0, 0],
    [
      225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78, 0, 0, 0, 0, 0,
      0,
    ],
    [
      224, 227, 226, 0, 0, 0, 44, 0, 0, 230, 61697, 0, 80, 81, 79, 0, 0, 0, 0,
      0, 0,
    ],
  ],
  /** Tank68 HE：华奋达 68 磁轴，右侧导航列同 BAT68，底行含 R-Ctrl */
  tank68he: [
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [
      41, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 0, 0, 0, 0, 0,
      0,
    ],
    [
      43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 77, 0, 0, 0, 0, 0,
      0,
    ],
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 75, 0, 0, 0, 0, 0, 0],
    [
      225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78, 0, 0, 0, 0, 0,
      0,
    ],
    [
      224, 227, 226, 0, 0, 0, 44, 0, 0, 230, 61697, 228, 80, 81, 79, 0, 0, 0, 0,
      0, 0,
    ],
  ],
  /**
   * 68 键盘布局 (6 行 × 15 列)
   */
  "68EVO": [
    // Row 0: 全空
    [0],
    // Row 1: Esc + 数字行 + Home (15 keys)
    [41, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74],
    // Row 2: Tab 行 + End (15 keys)
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76],
    // Row 3: Caps 行 + PageUp (15 keys)
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 0, 75],
    // Row 4: Shift 行 + 方向上 + PageDown (15 keys)
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 0, 78],
    // Row 5: 控制行 + 方向键 (15 keys)
    [224, 227, 226, 0, 44, 0, 44, 0, 44, 230, 61697, 228, 80, 81, 79],
  ],
  /** 84 键（SMART875） */
  "84": [
    [
      41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 70, 72, 0, 0,
      0, 0,
    ],
    [
      53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 75, 0, 0, 0,
      0, 0,
    ],
    [
      43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 77, 78, 0, 0, 0, 0,
      0,
    ],
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 0, 0, 0, 0, 0, 0, 0],
    [
      225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 0, 82, 0, 0, 0, 0,
      0,
    ],
    [
      224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 0, 0, 0,
      0, 0,
    ],
  ],
  /** DEEP80 MAX：虚拟布局与 SMART875(84) 一致 */
  deep80max: [
    [
      41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 70, 72, 0, 0,
      0, 0,
    ],
    [
      53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 75, 0, 0, 0,
      0, 0,
    ],
    [
      43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 77, 78, 0, 0, 0, 0,
      0,
    ],
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 0, 0, 0, 0, 0, 0, 0],
    [
      225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 0, 82, 0, 0, 0, 0,
      0,
    ],
    [
      224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 0, 0, 0,
      0, 0,
    ],
  ],
  /**
   * 87 TKL 键盘布局 (6 行 × 17 列)
   */
  "87": [
    // Row 0: Esc + F1-F12 + 系统键 (16 keys)
    [41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 0],
    // Row 1: 数字行 + 导航键 (17 keys)
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 74, 75],
    // Row 2: Tab 行 + 导航键 (17 keys)
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 77, 78],
    // Row 3: Caps 行 (13 keys)
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 0, 0, 0, 0],
    // Row 4: Shift 行 + 方向上 (13 keys)
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 0, 0, 0, 0],
    // Row 5: 控制行 + 方向键 (17 keys)
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 101, 228, 80, 81, 79],
    //                         ↑Space          ↑AltR ↑Fn   ↑Menu ↑RCtrl ← ↓ →
  ],
  /**
   * ES87 TKL 灯光/布局矩阵 (6×21，与 LAYOUT_ES87_KEYCODES 一致)
   */
  es87: [
    [
      41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 104, 70, 71, 72, 0, 0,
      0, 0,
    ],
    [
      53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 74, 75, 0, 0,
      0, 0,
    ],
    [
      43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 77, 78, 0, 0,
      0, 0,
    ],
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 0, 0, 0, 0, 0, 0, 0],
    [
      225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 0, 82, 0, 0, 0, 0,
      0,
    ],
    [
      224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 101, 228, 80, 81, 79, 0,
      0, 0, 0,
    ],
  ],
  huafenda87: [
    // Row 0: Esc + F1-F12 + PrtScr/Scroll/Pause (21 cols)
    [
      41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 0, 0, 0,
      0, 0,
    ],
    // Row 1: 数字行 + Ins/Home/PgUp (21 cols)
    [
      53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 74, 75, 0, 0,
      0, 0,
    ],
    // Row 2: Tab 行 + Del/End/PgDn (21 cols)
    [
      43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 77, 78, 0, 0,
      0, 0,
    ],
    // Row 3: Caps 行 + Enter (21 cols, Enter 后为占位)
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 0, 0, 0, 0, 0, 0, 0, 0],
    // Row 4: Shift 行 + R-Shift 后占位 + ↑ (21 cols)
    [
      225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 0, 82, 0, 0, 0, 0, 0,
      0,
    ],
    // Row 5: 控制行 + Alt/Fn/Ctrl + ←↓→ (21 cols)
    [
      224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 101, 228, 80, 81, 79, 0,
      0, 0, 0,
    ],
  ],
  rongyuan87: LAYOUT_RONGYUAN87_KEYCODES,
  /**
   * 星闪 X75 V3 键盘布局 (6 行 × 16 列 F 行)
   */
  x75v3: [
    [41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 73],
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74],
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76],
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 75],
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78],
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79],
  ],
  /**
   * EXX Gamma75：75% 紧凑配列，与 X75 V3 同构（无 App/Menu 键）
   */
  gamma75: [
    [41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 73],
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74],
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76],
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 75],
    [225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78],
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79],
  ],
  /**
   * DK75 标准款：F 行末 PrtScr + 旋钮占位
   */
  dk75: [
    [41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 0],
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74],
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76],
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 75],
    [225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78],
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 80, 81, 79],
  ],
  /**
   * DK75 Pro / ZE75（星闪 75 键，F 行末小屏+旋钮占位）
   */
  dk75Pro: [
    [41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 0, 0],
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74],
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76],
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 75],
    [225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78],
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 80, 81, 79],
  ],
  /** 智慧无限 DK87：6×17 出厂默认；运行时优先 0x17，失败降级 0x16 / 本地快照 */
  dk87: LAYOUT_DK87_KEYCODES,
  /** 智慧无限 DK75 Single：6×17，由协议出厂矩阵解析（见 zhwxDk75SingleLayout.ts） */
  dk75Single: LAYOUT_DK75_SINGLE_KEYCODES,
  /**
   * DK75 E（星闪 75 键，F 行末 PrtScr + Insert）
   */
  dk75E: [
    [41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 73],
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74],
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76],
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 75],
    [225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78],
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 80, 81, 79],
  ],
  /**
   * 75 Pro 键盘布局 (6 行 × 15 列)
   */
  "75Pro": [
    // Row 0: Esc + F1-F12 + 系统键 (15 keys)
    [41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 62208],
    // Row 1: 数字行 + 导航键 (15 keys)
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74],
    // Row 2: Tab 行 + 导航键 (15 keys)
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76],
    // Row 3: Caps 行 (14 keys)
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 75],
    // Row 4: Shift 行 + 方向上 (14 keys)
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78],
    // Row 5: 控制行 + 方向键 (10 keys)
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79],
  ],
  /**
   * X75V2（贝盈 916 机甲）75 键 — Ins + 旋钮，右侧 Del / PgUp / PgDn 导航列
   */
  x75v2: [
    // Row 0: Esc + F1-F12 + Ins + 旋钮 (17 keys)
    [41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 73, 0, 8418],
    // Row 1: 数字行 + Del (15 keys)
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 76],
    // Row 2: Tab 行 + PgUp (15 keys)
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 75],
    // Row 3: Caps 行 + PgDn (14 keys)
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 78],
    // Row 4: Shift 行 + 方向上 (13 keys)
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82],
    // Row 5: 控制行 + 方向键（无 R-Alt；Fn / R-Ctrl 加宽）
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 0, 61697, 228, 80, 81, 79],
  ],
  /**
   * X75GC（华丰达）75 键 — PrtSc + Home + 右侧 Del/PgUp/PgDn
   */
  x75gc: [
    [41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 74],
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 76],
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 75],
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 78],
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82],
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 230, 61697, 0, 80, 81, 79],
  ],
  m75: [
    // Row 0: Esc + F1-F12 + Pause + 旋钮 (17 keys)
    [41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 72, 0, 8418],
    // Row 1: 数字行 + Ins (15 keys)
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73],
    // Row 2: Tab 行 + Del (15 keys)
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76],
    // Row 3: Caps 行 + PgUp (14 keys)
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 75],
    // Row 4: Shift 行 + ↑ + PgDn (14 keys)
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78],
    // Row 5: 控制行 + 方向键 (16 keys)
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79],
  ],
  /**
   * M98 键盘布局 (6 行 × 18 列)
   */
  M98: [
    // Row 0: Esc + F1-F12 + 系统键 + 旋钮键 (18 keys)
    [41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 75, 78, 74, 8418],
    // Row 1: 数字行 + 导航键 (15 keys)
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 83, 84, 85, 86],
    // Row 2: Tab 行 + 导航键 (15 keys)
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 95, 96, 97, 87],
    // Row 3: Caps 行 (14 keys)
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 92, 93, 94, 0],
    // Row 4: Shift 行 + ↑ 1 2 3 Enter
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 89, 90, 91, 88],
    // Row 5: 控制行 + 方向键 + 小键盘 0 /.
    [
      224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 98, 99,
      0,
    ],
  ],
  /**
   * M98 屏幕版 键盘布局 (6 行 × 18 列)
   */
  M98TFT: [
    // Row 0: Esc + F1-F12 + 系统键 + 旋钮键 (18 keys)
    [41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 0, 0, 0, 8418],
    // Row 1: 数字行 + 导航键 (15 keys)
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 83, 84, 85, 86],
    // Row 2: Tab 行 + 导航键 (15 keys)
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 95, 96, 97, 87],
    // Row 3: Caps 行 (14 keys)
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 92, 93, 94, 0],
    // Row 4: Shift 行 + ↑ 1 2 3 Enter
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 89, 90, 91, 88],
    // Row 5: 控制行 + 方向键 + 小键盘 0 /.
    [
      224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 98, 99,
      0,
    ],
  ],
  /**
   * M98 8K 键盘布局 (6 行 × 18 列)
   */
  "M98 8K": [
    // Row 0: Esc + F1-F12 + 系统键 + 旋钮键 (18 keys)
    [41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 0, 0, 0, 8418],
    // Row 1: 数字行 + 导航键 (15 keys)
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 83, 84, 85, 86],
    // Row 2: Tab 行 + 导航键 (15 keys)
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 95, 96, 97, 87],
    // Row 3: Caps 行 (14 keys)
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 92, 93, 94, 0],
    // Row 4: Shift 行 + ↑ 1 2 3 Enter
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 89, 90, 91, 88],
    // Row 5: 控制行 + 方向键 + 小键盘 0 /.
    [
      224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 98, 99,
      0,
    ],
  ],
  /**
   * 75 Pro TFT 键盘布局 (6 行 × 15 列)
   */
  "75ProTFT": [
    // Row 0: Esc + F1-F12 + 系统键 (15 keys)
    [41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 62208],
    // Row 1: 数字行 + 导航键 (15 keys)
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74],
    // Row 2: Tab 行 + 导航键 (15 keys)
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76],
    // Row 3: Caps 行 (13 keys)
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 0],
    // Row 4: Shift 行 + 方向上 (13 keys)
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 0],
    // Row 5: 控制行 + 方向键 (10 keys)
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79],
  ],
  /**
   * 99 键盘布局 (6 行 × 21 列)
   */
  "99": [
    [
      41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 73, 74, 75, 78,
      0, 0,
    ], // 19 补2个0
    [
      53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 83, 84, 85, 86, 0,
      0, 0,
    ], // 18 补3个0
    [
      43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 95, 96, 97, 87, 0,
      0, 0,
    ], // 18 补3个0
    [
      57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 92, 93, 94, 0, 0, 0, 0,
      0,
    ], // 16 补5个0
    [
      225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 0, 82, 89, 90, 91,
      88, 0,
    ], // 20 补1个0
    [
      224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 98, 99,
      0, 0, 0,
    ], // 10 补11个0
  ],
  /**
   * 98 键盘布局 (6 行 × 19 列)
   */
  "98": [
    // Row 0: Esc + F1-F12 + 系统键 (18 keys)
    [
      41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 8594, 74, 77,
      72,
    ],
    // Row 1: 数字行 + 导航键 (19 keys)
    [
      53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 83, 84, 85,
      86,
    ],
    // Row 2: Tab 行 + 导航键 (19 keys)
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 75, 95, 96, 97, 87],
    // Row 3: Caps 行 (17 keys)
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 78, 92, 93, 94, 0],
    // Row 4: Shift 行 + 方向上 (17 keys)
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 0, 89, 90, 91, 88],
    // Row 5: 控制行 + 方向键 (11 keys)
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 80, 81, 79, 98, 99, 0],
    //                       ↑Space        ↑AltR ↑Fn
  ],
  /**
   * 贝盈 X98 1K：与 LAYOUT_98_KEYCODES 同序同槽位，供 getKeyboardKeys({layer}) 的 pos
   * 与分位画板一致，勿与上方「98」19 列简表混用行尾占位。
   */
  X98_1K: LAYOUT_X98_1K_KEYCODES.map((row) => [...row]),
  /**
   *  M80（机甲 916）6 行 × 19 列 — 星闪/项目 KEYCODE_MAP 数字 id，与 Untitled 物理排布一致
   */
  m80: [
    [41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 76, 0, 0, 0, 0],
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 74, 0, 0, 0, 0],
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 77, 0, 0, 0, 0],
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 75, 0, 0, 0, 0, 0],
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 78, 0, 0, 0, 0],
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 228, 80, 81, 79, 0, 0, 0],
  ],
  /**
   * M71（机甲）6 行 × 19 列 — 默认与 M80 同构物理矩阵；若实机键位不同，仅改本项即可
   */
  m71: [
    [0],
    [41, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 74, 0, 0, 0],
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 77, 75, 0, 0, 0],
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 76, 78, 0, 0, 0, 0],
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 0, 0, 0, 0],
    [
      224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 21252, 228, 80, 81, 79,
      0, 0, 0,
    ],
  ],
  /**
   * T87（贝盈机甲）6 行 × 17 列 — 物理配列复用 X87 (huafenda87)
   */
  t87: [
    // Row 0: Esc + F1-F12 + 系统键 (16 keys)
    [41, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 0],
    // Row 1: 数字行 + 导航键 (17 keys)
    [53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 74, 75],
    // Row 2: Tab 行 + 导航键 (17 keys)
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 77, 78],
    // Row 3: Caps 行 (13 keys)
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 40, 0, 0, 0, 0],
    // Row 4: Shift 行 + 方向上 (13 keys)
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 82, 0, 0, 0],
    // Row 5: 控制行 + 方向键 (17 keys)
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 101, 228, 80, 81, 79],
  ],
  /** DK108：108 键全尺寸 ANSI（6×21）；已与实机 SDK getKeyData 校准通过 */
  DK108: [
    [
      41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 8374,
      8397, 8373, 62208,
    ],
    [
      53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 74, 75, 83,
      84, 85, 86,
    ],
    [
      43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 76, 77, 78, 95, 96,
      97, 87,
    ],
    [
      57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 0, 0, 0, 92, 93, 94,
      0,
    ],
    [
      225, 0, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 0, 0, 82, 0, 89, 90,
      91, 88,
    ],
    [
      224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 230, 61697, 101, 228, 80, 81, 79, 98,
      99, 0, 0,
    ],
  ],
  huafenda98: [
    // Row 0: Esc + F1-F12 + 系统键 (18 keys)
    [
      41, 0, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 76, 8594, 74, 77,
      72,
    ],
    // Row 1: 数字行 + 导航键 (19 keys)
    [
      53, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 45, 46, 42, 73, 83, 84, 85,
      86,
    ],
    // Row 2: Tab 行 + 导航键 (19 keys)
    [43, 20, 26, 8, 21, 23, 28, 24, 12, 18, 19, 47, 48, 49, 75, 95, 96, 97, 87],
    // Row 3: Caps 行 (17 keys)
    [57, 4, 22, 7, 9, 10, 11, 13, 14, 15, 51, 52, 0, 40, 78, 92, 93, 94, 0],
    // Row 4: Shift 行 + 方向上 (17 keys)
    [225, 29, 27, 6, 25, 5, 17, 16, 54, 55, 56, 229, 82, 0, 89, 90, 91, 88],
    // Row 5: 控制行 + 方向键 (11 keys)
    [224, 227, 226, 0, 0, 0, 44, 0, 0, 0, 228, 61697, 80, 81, 79, 98, 99, 0],
    //                       ↑Space       ↑AltR ↑Fn
  ],
  "104": [],
  keymap104: [],
  media: [],
  light: [],
  control: [],
  mouse: [],
  macro: [],
};
/**
 * 获取 KeyCode 信息
 */
export const getKeyCodeInfo = (keycode: number): KeyCodeInfo | null => {
  return KEYCODE_MAP[keycode] || KEYCODE_MACRO_MAP[keycode] || null;
};

/**
 * KeyCode → 显示名称
 */
export const getKeyName = (keycode: number): string => {
  return (
    KEYCODE_MAP[keycode]?.name ||
    KEYCODE_MACRO_MAP[keycode]?.name ||
    `Unknown(${keycode})`
  );
};

/**
 * KeyCode → Web标准 KeyCode (用于灯光映射)
 */
export const KEYCODE_TO_WEB_KEY_MAP: Record<number, string> = {
  // 字母
  4: "KeyA",
  5: "KeyB",
  6: "KeyC",
  7: "KeyD",
  8: "KeyE",
  9: "KeyF",
  10: "KeyG",
  11: "KeyH",
  12: "KeyI",
  13: "KeyJ",
  14: "KeyK",
  15: "KeyL",
  16: "KeyM",
  17: "KeyN",
  18: "KeyO",
  19: "KeyP",
  20: "KeyQ",
  21: "KeyR",
  22: "KeyS",
  23: "KeyT",
  24: "KeyU",
  25: "KeyV",
  26: "KeyW",
  27: "KeyX",
  28: "KeyY",
  29: "KeyZ",

  // 数字
  30: "Digit1",
  31: "Digit2",
  32: "Digit3",
  33: "Digit4",
  34: "Digit5",
  35: "Digit6",
  36: "Digit7",
  37: "Digit8",
  38: "Digit9",
  39: "Digit0",

  // 功能键
  40: "Enter",
  41: "Escape",
  42: "Backspace",
  43: "Tab",
  44: "Space",
  57: "CapsLock",

  // F 键
  58: "F1",
  59: "F2",
  60: "F3",
  61: "F4",
  62: "F5",
  63: "F6",
  64: "F7",
  65: "F8",
  66: "F9",
  67: "F10",
  68: "F11",
  69: "F12",
  104: "F13",

  // 符号
  45: "Minus",
  46: "Equal",
  47: "BracketLeft",
  48: "BracketRight",
  49: "Backslash",
  51: "Semicolon",
  52: "Quote",
  53: "Backquote",
  54: "Comma",
  55: "Period",
  56: "Slash",

  // 导航
  73: "Insert",
  74: "Home",
  75: "PageUp",
  76: "Delete",
  77: "End",
  78: "PageDown",
  79: "ArrowRight",
  80: "ArrowLeft",
  81: "ArrowDown",
  82: "ArrowUp",

  // 小键盘
  83: "NumpadLock",
  84: "NumpadDivide",
  85: "NumpadMultiply",
  86: "NumpadSubtract",
  87: "NumpadAdd",
  88: "NumpadEnter",
  89: "Numpad1",
  90: "Numpad2",
  91: "Numpad3",
  92: "Numpad4",
  93: "Numpad5",
  94: "Numpad6",
  95: "Numpad7",
  96: "Numpad8",
  97: "Numpad9",
  98: "Numpad0",
  99: "NumpadDecimal",
  100: "NumpadClear",

  // 修饰键
  224: "ControlLeft",
  225: "ShiftLeft",
  226: "AltLeft",
  227: "MetaLeft",
  228: "ControlRight",
  229: "ShiftRight",
  230: "AltRight",
  231: "MetaRight",

  // 系统键
  70: "PrintScreen",
  71: "ScrollLock",
  72: "Pause",
  101: "ContextMenu",

  // Fn 键
  61696: "Fn0",
  61697: "Fn1",
  61698: "Fn2",
  61699: "Fn3",
  8594: "Calc",
  /** 8815 固件计算器 value，与 8594 同键语义 */
  33554834: "Calc",
  23205: "Unidentified",
  62208: "Mode",
};

/**
 * Web标准 KeyCode → SDK KeyCode
 */
export const WEB_KEY_TO_KEYCODE_MAP: Record<string, number> =
  Object.fromEntries(
    Object.entries(KEYCODE_TO_WEB_KEY_MAP).map(([k, v]) => [v, Number(k)])
  );

/**
 * 判断是否为空键
 */
export const isEmptyKey = (keycode: number): boolean => {
  return keycode === 0;
};

/**
 * 判断是否为穿透键
 */
export const isPierceKey = (keycode: number): boolean => {
  return keycode === 1;
};

export const getlabelToKeyCode = (label: string | number) => {
  const lbl = typeof label === "number" ? String(label) : label;
  for (const [key, value] of Object.entries(KEYCODE_MAP)) {
    if (value.name === lbl && value.typeof !== "game") {
      return key;
    }
  }
  for (const [key, value] of Object.entries(KEYCODE_MACRO_MAP)) {
    if (value.name === lbl) {
      return key;
    }
  }
  return "";
};
export const getKeyCodeToLabel = (
  keyCode: number | string,
  type: number = 1
) => {
  let label: any = "";
  let item: any = "";
  for (const [key, value] of Object.entries(KEYCODE_MAP)) {
    if (Number(key) === keyCode && value.typeof === "basic") {
      label = value.name;
      item = value;
    }
  }
  if (type === 1) {
    return label;
  } else if (type === 2) {
    return item;
  }
};

/**
 * HFD 键码映射数据结构
 */
export interface HfdKeyCodeInfo {
  name: string;
  type: string;
  comm: string;
  hex16: string;
  typeof: string;
  value?: number;
  userKey?: object;
}
/**
 * 将 HFD 键码映射数据转换为标准 KeyCodeInfo 格式
 * @param hfdKeyCodeMap - 来自 keyCodeMapperHfd.ts 的 KEYCODE_MAP 数据
 * @returns 转换后的标准 KeyCodeInfo 映射表
 */
export const convertHfdKeyCodeMapToStandard = (
  hfdKeyCodeMap: Record<string, HfdKeyCodeInfo>
): Record<number, KeyCodeInfo> => {
  const result: Record<number, KeyCodeInfo> = {};

  for (const [key, value] of Object.entries(hfdKeyCodeMap)) {
    const keyCodeNumber = Number(key);

    // 尝试将 HFD 的 typeof 映射到标准的 typeof 类型
    let standardTypeof:
      | "basic"
      | "special"
      | "system"
      | "media"
      | "mouse"
      | "control"
      | "light"
      | "triMode"
      | "macro"
      | "game";
    switch (value.typeof) {
      case "basic":
      case "special":
      case "system":
      case "media":
      case "mouse":
      case "control":
      case "light":
      case "triMode":
      case "macro":
      case "game":
        standardTypeof = value.typeof;
        break;
      default:
        // 如果 HFD 的类型不在标准类型中，默认为 'basic'
        standardTypeof = "basic";
        break;
    }

    result[keyCodeNumber] = {
      name: value.name,
      type: value.type,
      comm: value.comm,
      hex16: value.hex16,
      typeof: standardTypeof,
    };
  }

  return result;
};

/**
 * 将标准 KeyCodeInfo 映射表转换为 HFD 格式
 * @param standardKeyCodeMap - 标准的 KEYCODE_MAP 数据
 * @returns 转换后的 HFD 格式映射表
 */
export const convertStandardKeyCodeMapToHfd = (
  standardKeyCodeMap: Record<number, KeyCodeInfo>
): Record<string, HfdKeyCodeInfo> => {
  const result: Record<string, HfdKeyCodeInfo> = {};

  for (const [key, value] of Object.entries(standardKeyCodeMap)) {
    const keyCodeString = String(key);

    result[keyCodeString] = {
      name: value.name,
      type: value.type,
      comm: value.comm,
      hex16: value.hex16,
      typeof: value.typeof,
    };
  }

  return result;
};

/**
 * 合并 HFD 键码映射数据到标准映射表中
 * 如果标准映射表中已存在对应键码，则优先使用标准数据
 * @param hfdKeyCodeMap - 来自 keyCodeMapperHfd.ts 的 KEYCODE_MAP 数据
 * @param standardKeyCodeMap - 标准的 KEYCODE_MAP 数据（可选，如果不提供则使用默认的 KEYCODE_MAP）
 * @returns 合并后的标准 KeyCodeInfo 映射表
 */
export const mergeHfdKeyCodeMapWithStandard = (
  hfdKeyCodeMap: Record<string, HfdKeyCodeInfo>,
  standardKeyCodeMap: Record<number, KeyCodeInfo> = KEYCODE_MAP
): Record<number, KeyCodeInfo> => {
  const result: Record<number, KeyCodeInfo> = { ...standardKeyCodeMap };

  for (const [key, value] of Object.entries(hfdKeyCodeMap)) {
    const keyCodeNumber = Number(key);

    // 如果标准映射表中不存在该键码，则添加 HFD 数据
    if (!result[keyCodeNumber]) {
      let standardTypeof:
        | "basic"
        | "special"
        | "system"
        | "media"
        | "mouse"
        | "control"
        | "light"
        | "triMode"
        | "macro"
        | "game";
      switch (value.typeof) {
        case "basic":
        case "special":
        case "system":
        case "media":
        case "mouse":
        case "control":
        case "light":
        case "triMode":
        case "macro":
        case "game":
          standardTypeof = value.typeof;
          break;
        default:
          standardTypeof = "basic";
          break;
      }

      result[keyCodeNumber] = {
        name: value.name,
        type: value.type,
        comm: value.comm,
        hex16: value.hex16,
        typeof: standardTypeof,
      };
    }
  }

  return result;
};

/**
 * 根据标准KeyCode获取HFD格式的键码信息
 * @param standardKeyCode - 标准KeyCode值（如23）
 * @param standardKeyCodeMap - 标准KeyCode映射表
 * @param hfdKeyCodeMap - HFDKeyCode映射表
 * @returns HFD格式的键码信息对象，如果未找到则返回null
 */
type HfdKeyCodeInfoRes = {
  value: number;
  userKey: {
    name: string;
    value: number | string;
    page: string;
  };
};
export const getHfdInfoByStandardKeyCode = (
  standardKeyCode: number,
  standardKeyCodeMap: Record<number, KeyCodeInfo> = KEYCODE_MAP
  // hfdKeyCodeMap: any = KEYCODE_MAP_HFD // 需要导入keyCodeMapperHfd中的KEYCODE_MAP
): HfdKeyCodeInfoRes | null => {
  // 从标准映射表中获取键名
  const standardInfo = standardKeyCodeMap[standardKeyCode];
  if (!standardInfo) {
    return null;
  }

  // const keyName = standardInfo.name;
  // 在HFD映射表中通过键名查找对应的键码
  // for (const [hfdKeyCode, hfdInfo] of Object.entries(hfdKeyCodeMap)) {
  //   if (hfdInfo?.name === keyName) {
  //     const obj: HfdKeyCodeInfoRes = {
  //       value: Number(hfdKeyCode),
  //       userKey: {
  //         name: hfdInfo.name,
  //         value: hfdInfo.hex16,
  //         page: "KEYBOARD",
  //       },
  //     };
  //     return obj;
  //   }
  // }

  return null;
};

/**
 * 根据标准KeyCode获取HFD的KeyCode值
 * @param standardKeyCode - 标准KeyCode值（如23）
 * @param standardKeyCodeMap - 标准KeyCode映射表
 * @param hfdKeyCodeMap - HFDKeyCode映射表
 * @returns HFD的KeyCode值，如果未找到则返回null
 */
export const getHfdKeyCodeByStandardKeyCode = (
  standardKeyCode: number,
  standardKeyCodeMap: Record<number, KeyCodeInfo> = KEYCODE_MAP
  // hfdKeyCodeMap: any = KEYCODE_MAP_HFD // 需要导入keyCodeMapperHfd中的KEYCODE_MAP
): number | null => {
  // 从标准映射表中获取键名
  const standardInfo = standardKeyCodeMap[standardKeyCode];
  if (!standardInfo) {
    return null;
  }

  // const keyName = standardInfo.name;

  // 在HFD映射表中通过键名查找对应的键码
  // for (const [hfdKeyCode, hfdInfo] of Object.entries(hfdKeyCodeMap)) {
  //   if (hfdInfo.name === keyName) {
  //     return Number(hfdKeyCode);
  //   }
  // }

  return null;
};

/**
 * 通过行列值获取HFD格式的按键信息
 * @param row - 行索引
 * @param col - 列索引
 * @param keyboardType - 键盘类型，默认为'87'
 * @returns HFD格式的按键信息对象
 */
export const getHfdKeyInfoByPosition = (
  row: number,
  col: number,
  keyboardData: any
): HfdKeyCodeInfoRes | null => {
  try {
    // 从键盘布局中获取对应位置的keyCode
    const layout = keyboardData;
    if (
      !layout ||
      row < 0 ||
      row >= layout.length ||
      col < 0 ||
      col >= layout[row].length
    ) {
      console.warn(`Invalid position: row=${row}, col=${col}`);
      return null;
    }

    const keyCode = layout[row][col];
    // 如果keyCode为0（空键），返回null
    if (keyCode === 0) {
      return null;
    }

    // 从标准KeyCode映射表中获取按键名称
    const standardKeyCodeInfo = KEYCODE_MAP[keyCode];
    if (!standardKeyCodeInfo) {
      console.warn(`No standard key info found for keyCode: ${keyCode}`);
      return null;
    }

    const keyName = standardKeyCodeInfo.name;

    // 在HFD KeyCode映射表中查找对应名称的按键
    let targetHfdInfo: { keycode: number; info: any } | null = null;

    for (const [keycode, info] of Object.entries(KEYCODE_MAP_HFD)) {
      if (info.name === keyName) {
        targetHfdInfo = {
          keycode: Number(keycode),
          info: info,
        };
        break;
      }
    }

    if (!targetHfdInfo) {
      console.warn(`No HFD key info found for name: ${keyName}`);
      return null;
    }

    // 构造并返回所需的格式
    return {
      value: targetHfdInfo.keycode,
      userKey: {
        name: targetHfdInfo.info.name,
        value: targetHfdInfo.info.hex16,
        page: "KEYBOARD",
      },
    };
  } catch (error) {
    console.error("Error getting HFD key info by position:", error);
    return null;
  }
};
